import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import SchemaOrg from "@/components/base/SchemaOrg";
import { articles, categories } from "@/mocks/siteData";

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as const });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-12 px-6 md:px-10 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">文章未找到</h1>
          <Link to="/" className="text-accent-500 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.id === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <SchemaOrg
        type="Article"
        articleData={{
          headline: article.title,
          description: article.excerpt,
          image: article.imageUrl,
          datePublished: "2024-06-15T08:00:00+08:00",
          dateModified: "2024-12-20T10:30:00+08:00",
          authorName: "白丝指南编辑团队",
          url: `https://baisi-guide.com/article/${article.slug}`,
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <article className="pt-20 pb-12 px-6 md:px-10">
          <div className="max-w-3xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                { label: article.title },
              ]}
            />

            <header className="mb-8">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="inline-block text-xs text-accent-500 bg-accent-50 px-3 py-1 rounded-full mb-4 hover:bg-accent-100 transition-colors duration-200"
                >
                  {category.name}
                </Link>
              )}
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
                {article.title}
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>
            </header>

            <div className="relative w-full h-[300px] md:h-[420px] rounded-lg overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-base max-w-none text-foreground-800"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200">
              <p className="text-xs text-foreground-500 mb-1">发布时间：2024年6月15日</p>
              <p className="text-xs text-foreground-500">最后更新：2024年12月20日</p>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 px-6 md:px-10 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <ArticleCard
                    key={ra.id}
                    slug={ra.slug}
                    title={ra.title}
                    excerpt={ra.excerpt}
                    category={ra.category}
                    imageUrl={ra.imageUrl}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </div>
    </>
  );
}